import {
  flexRender,
  HeaderGroup,
  Row,
  RowData,
  Table,
} from "@tanstack/react-table";
import React from "react";
import Carat from "../../Toolbar/Carat";

type TableGroup = "center" | "left" | "right";

function getTableHeaderGroups<T extends RowData>(
  table: Table<T>,
  tg?: TableGroup
): [HeaderGroup<T>[], HeaderGroup<T>[]] {
  if (tg === "left") {
    return [table.getLeftHeaderGroups(), table.getLeftFooterGroups()];
  }

  if (tg === "right") {
    return [table.getRightHeaderGroups(), table.getRightFooterGroups()];
  }

  if (tg === "center") {
    return [table.getCenterHeaderGroups(), table.getCenterFooterGroups()];
  }

  return [table.getHeaderGroups(), table.getFooterGroups()];
}

function getRowGroup<T extends RowData>(row: Row<T>, tg?: TableGroup) {
  if (tg === "left") return row.getLeftVisibleCells();
  if (tg === "right") return row.getRightVisibleCells();
  if (tg === "center") return row.getCenterVisibleCells();
  return row.getVisibleCells();
}

type Props<T extends RowData> = {
  isSelectionEnabled?: boolean;
  table: Table<T>;
  tableGroup?: TableGroup;
  highlightedOids?: number[];
  onRowClick?: (row: Row<T>) => void;
  onRowHover?: (row?: Row<T>) => void;
  isFixedTable?: boolean;
};

export function CustomTable<T extends RowData>({
  table,
  tableGroup,
  highlightedOids,
  onRowClick,
  onRowHover,
  ...props
}: Props<T>) {
  const [headerGroups] = getTableHeaderGroups(table, tableGroup);

  return (
    <>
      <table
        className="min-w-full bg-slate-400 border border-[#4F4E4E] rounded-lg overflow-hidden"
        style={{
          width: table.getCenterTotalSize(),
          tableLayout: props.isFixedTable ? "fixed" : "auto",
        }}
      >
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header, i) => (
                <th
                  className={`${i === 0 ? "rounded-tl-lg " : ""} ${i === headerGroup.headers.length - 1
                    ? "rounded-tr-lg"
                    : "border-r"
                    } relative bg-[#4F4E4E] text-white p-1 text-xs border-[#A5A5A5]`}
                  key={header.id}
                  style={{
                    width: header.getSize(),
                  }}
                  colSpan={header.colSpan}
                >
                  {header.isPlaceholder ? null : (
                    <>
                      <div
                        className={
                          header.column.getCanSort()
                            ? "cursor-pointer select-none flex items-center justify-between px-1"
                            : ""
                        }
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}{" "}
                        <button>
                          {{
                            asc: <Carat direction="up" color="white" />,
                            desc: <Carat direction="down" color="white" />,
                          }[header.column.getIsSorted() as string] ?? " "}
                        </button>
                      </div>
                    </>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="">
          {table.getRowModel().rows.map((row, i) => (
            <tr
              onMouseLeave={() => onRowHover?.()}
              onMouseOver={() => onRowHover?.(row)}
              onClick={() => onRowClick?.(row)}
              key={row.id}
              className={`hover:bg-[#FFC0CC] focus:[#BFD6EB] ${i % 2 === 0 ? "bg-[#F9F6DF]" : "bg-[#FFFFFF]"
                } ${highlightedOids?.includes(row.original["OBJECTID"])
                  ? "bg-[#FFC0CC]"
                  : ""
                }`}
            >
              {getRowGroup(row, tableGroup).map((cell) => (
                <td
                  className={`border-r border-[#A5A5A5] p-1 text-xs ${props.isFixedTable ? "break-words" : ""
                    }`}
                  key={cell.id}
                  style={{
                    width: cell.column.getSize() + "px",
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

export default CustomTable;
